<template>
  <transition name="fade">
    <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-50 p-4 sm:p-6">
      <div class="relative w-full max-w-md rounded-lg bg-white shadow-xl ring-1 ring-gray-200">
        
        <!-- Header -->
        <div class="flex items-center justify-between border-b px-4 py-3 bg-gray-50 rounded-t-lg">
          <h3 class="text-lg font-medium text-gray-900">
            Lien de réinitialisation de mot de passe
          </h3>
          <button @click="close" class="text-gray-400 hover:text-gray-500 focus:outline-none">
            <span class="sr-only">Fermer</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-4">
          <div v-if="loading" class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p class="mt-2 text-sm text-gray-600">Génération du lien...</p>
          </div>

          <div v-else-if="error" class="text-center py-4">
            <div class="text-red-600 text-sm">{{ error }}</div>
          </div>

          <div v-else-if="resetData" class="space-y-4">
            <!-- Informations client -->
            <div class="bg-gray-50 p-4 rounded-lg">
              <h4 class="text-sm font-semibold text-gray-900 mb-2">Informations client</h4>
              <div class="space-y-1 text-sm text-gray-600">
                <p><span class="font-medium">Email :</span> {{ resetData.email }}</p>
                <p v-if="resetData.clientInfo">
                  <span class="font-medium">Nom :</span> {{ resetData.clientInfo.nom }} {{ resetData.clientInfo.prenom }}
                </p>
                <p v-if="resetData.clientInfo">
                  <span class="font-medium">Langue :</span> {{ resetData.clientInfo.langue }}
                </p>
                <p v-if="resetData.expiresIn">
                  <span class="font-medium">Expire dans :</span> {{ resetData.expiresIn }}
                </p>
              </div>
            </div>

            <!-- Lien de réinitialisation -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Lien de réinitialisation</label>
              <div class="flex gap-2">
                <input
                  type="text"
                  :value="resetData.resetLink"
                  readonly
                  ref="linkInput"
                  class="flex-1 border-gray-300 rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border bg-gray-50 font-mono text-xs"
                />
                <button
                  @click="copyLink"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  :class="{ 'bg-green-700': copied }"
                >
                  {{ copied ? '✓ Copié' : '📋 Copier' }}
                </button>
              </div>
            </div>

            <!-- Token (optionnel, pour debug) -->
            <details class="text-sm">
              <summary class="cursor-pointer text-gray-600 hover:text-gray-900">Afficher le token</summary>
              <div class="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <code class="text-xs break-all">{{ resetData.token }}</code>
              </div>
            </details>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 border-t bg-gray-50 px-4 py-3 rounded-b-lg">
          <button
            type="button"
            class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            @click="close"
            :disabled="loading"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from '../../plugins/axios';

export default {
  name: 'PasswordResetModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    client: {
      type: Object,
      default: null
    }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      resetData: null,
      copied: false
    };
  },
  watch: {
    visible(newVal) {
      if (newVal && this.client) {
        this.generateResetLink();
      } else {
        this.resetState();
      }
    }
  },
  methods: {
    close() {
      if (!this.loading) {
        this.$emit('close');
      }
    },
    resetState() {
      this.loading = false;
      this.error = null;
      this.resetData = null;
      this.copied = false;
    },
    async generateResetLink() {
      if (!this.client) return;

      this.loading = true;
      this.error = null;
      this.resetData = null;

      try {
        const response = await axios.post('/admin2/generate-password-reset-link', {
          email_gologin: this.client.Email_Gologin
        });

        if (response.data && response.data.success) {
          this.resetData = response.data;
        } else {
          this.error = response.data.error || 'Une erreur est survenue lors de la génération du lien.';
        }
      } catch (err) {
        console.error('Erreur génération lien reset :', err);
        this.error = err.response?.data?.error || err.message || 'Erreur lors de la génération du lien.';
      } finally {
        this.loading = false;
      }
    },
    async copyLink() {
      if (!this.resetData || !this.resetData.resetLink) return;

      try {
        await navigator.clipboard.writeText(this.resetData.resetLink);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        // Fallback pour les navigateurs qui ne supportent pas clipboard API
        const input = this.$refs.linkInput;
        if (input) {
          input.select();
          document.execCommand('copy');
          this.copied = true;
          setTimeout(() => {
            this.copied = false;
          }, 2000);
        }
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

